import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import FileCard from "../components/FileCard";
import { getFiles } from "../api";

export default function HomePage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#files') {
      const element = document.getElementById('files');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const res = await getFiles();
      setFiles(res.data.files);
    } catch (err) {
      setError("Failed to load files. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filtered = files.filter(
    (f) =>
      f.title.toLowerCase().includes(search.toLowerCase()) ||
      f.description.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Navbar />

      {/* Hero */}
      <div className="relative overflow-hidden">
        {/* Subtle gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-50/80 to-transparent pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-indigo-100/60 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-10 right-20 w-[200px] h-[200px] bg-violet-100/40 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute top-10 left-20 w-[200px] h-[200px] bg-blue-100/40 rounded-full blur-2xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center relative">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white border border-indigo-100 shadow-sm shadow-indigo-100/50 rounded-full px-4 py-1.5 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
            <span className="text-indigo-600 text-xs font-mono uppercase tracking-widest font-medium">
              Software Distribution Platform
            </span>
          </div>

          {/* Heading */}
          {/* <h1 className="text-5xl sm:text-6xl font-display font-bold text-gray-900 mb-4 tracking-tight">
            VIII<span className="text-indigo-600">bits</span> Secure
            <span className="text-indigo-600">Vault</span>
          </h1>
          <p className="text-gray-500 text-lg max-w-xl mx-auto leading-relaxed">
            Credential-protected file distribution. Every download is authenticated and tracked.
          </p> */}

                    <h1 className="text-5xl sm:text-6xl font-display font-bold text-gray-900 mb-4 tracking-tight">


            <span className="text-[#544895]">
             VIII
            </span>
            <span className="text-[#3465a4]">
             bits
            </span>{" "}
            <span className="text-[#544895]">
              Secure
            </span>{" "}
            <span className="text-[#3465a4]">
              Vault
            </span>
          </h1>


          <p className="text-gray-500 text-lg max-w-xl mx-auto leading-relaxed">
            Credential-protected file distribution. Every download is
            authenticated and tracked.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-md mx-auto relative">
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all duration-200"
              placeholder="Search files..."
            />
          </div>

          {/* Decorative bottom border */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-100 to-transparent" />
        </div>
      </div>

      {/* Files Grid */}
      <div id="files" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-10">
        {/* Stats bar */}
        <div className="flex items-center justify-between mb-8">
          <div className="text-gray-400 text-sm font-mono">
            {loading
              ? "—"
              : `${filtered.length} file${filtered.length !== 1 ? "s" : ""} available`}
          </div>
          <div className="h-px flex-1 bg-gray-100 mx-4" />
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs text-gray-400 font-mono">live</span>
          </div>
        </div>

        {/* Loading Skeleton */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm animate-pulse"
              >
                <div className="w-11 h-11 bg-gray-100 rounded-xl mb-4" />
                <div className="h-5 bg-gray-100 rounded-lg w-3/4 mb-3" />
                <div className="h-4 bg-gray-100 rounded-lg w-full mb-2" />
                <div className="h-4 bg-gray-100 rounded-lg w-5/6 mb-6" />
                <div className="h-10 bg-gray-100 rounded-xl" />
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-16">
            <div className="w-14 h-14 bg-red-50 border border-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <p className="text-red-500 font-mono text-sm mb-4">{error}</p>
            <button
              onClick={fetchFiles}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-24">
            <div className="w-16 h-16 bg-white border border-gray-100 shadow-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-7 h-7 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-gray-400 font-mono text-sm">
              {search
                ? "No files match your search."
                : "No files available yet."}
            </p>
          </div>
        )}

        {/* Files Grid */}
        {!loading && !error && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-in">
            {filtered.map((file) => (
              <FileCard key={file._id} file={file} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
